/* libs */
import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import includes from "lodash/includes";

/* components */
import formWrapper from "../../shared/Form";
import ChangeHistory from "../../shared/ChangeHistory";
import {
	TextInput,
	TextEditor,
	Tags,
	SelectInput,
	FileInput,
	DateTimePicker,
	PublishAtPicker,
	CheckBox,
	MultilineTextInput,
} from "../../shared/Forms";
import Loader from "../../ui/Loader";
import SessionSpeakers from "./SessionSpeakers";
import Locations from "./Locations";
import GetItem from "../../../helpers/dataProviders/getItem";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";

class SessionsForm extends Component {
	constructor(props) {
		super(props);
		this.appKey = parseAppKey();
		this.state = {
			sessionTags: [],
			tagsFetched: false,
		};
	}

	componentDidMount() {
		this.getSessionTags();
	}

	getSessionTags = () => {
		GetItem(`/apps/${this.appKey}/sessions`).then(data => {
			let sessionTags = [];
			let tags = {};
			data.data.forEach(item => {
				item.tags.forEach(tag => {
					tags[tag] = tag;
				});
			});
			Object.keys(tags).forEach(tag => {
				sessionTags.push({
					id: tag,
					label: tag,
				});
			});

			this.setState({
				sessionTags: sessionTags,
				tagsFetched: true,
			});
		});
	};

	onEndsAtFocus = fieldApi => {
		const { values } = this.props;
		const { startsAt } = values;
		if (!fieldApi.getValue()) {
			if (startsAt) {
				fieldApi.setValue(startsAt);
			}
		}
	};

	render() {
		const { topics, values, formApi, sessions, initialValues, actionType, id } = this.props;
		const { sessionTags, tagsFetched } = this.state;
		let data = [];
		if (topics && topics.length > 0) {
			data = topics.map(topic => ({
				key: topic.id,
				value: topic.id,
				label: topic.name,
			}));
		}
		let kindOptions = [
			{
				key: "simple",
				value: "simple",
				label: "Simple Session",
			},
			{
				key: "full",
				value: "full",
				label: "Full Session",
			},
		];
		let remindOptions = [
			{
				key: "no",
				value: "no",
				label: "Don't send a reminder",
			},
			{
				key: "yes",
				value: "yes",
				label: "Send a reminder",
			},
		];
		let sessionOptions = [];
		if (sessions && sessions.length > 0 && tagsFetched) {
			if (values.tags && values.tags.length > 0) {
				const currentSessionTags = values.tags ? values.tags : [];
				sessions.forEach(session => {
					const { tags, id, title } = session;
					const containsTags = currentSessionTags.every(currentSessionTag => {
						return includes(tags, currentSessionTag);
					});
					if (containsTags) {
						sessionOptions.push({
							key: id,
							value: id,
							label: title,
							group: "Similar Sessions",
						});
					}
				});
			}
			sessions.forEach(session => {
				const { id, title } = session;
				sessionOptions.push({
					key: id,
					value: id,
					label: title,
					group: "All Sessions",
				});
			});
		}
		const now = moment
			.utc()
			.minutes(0)
			.seconds(0)
			.milliseconds(0)
			.add(1, "hour");
		const { startsAt } = initialValues;
		const defaultDate = startsAt ? startsAt : now;

		return (
			<div>
				{actionType === "update" && !!id && <ChangeHistory entity="sessions" id={id} />}
				<SelectInput field="kind" id="kind" values={kindOptions} />
				<TextInput field="title" id="title" placeholder="Title" autoComplete="off" />
				<DateTimePicker
					field="startsAt"
					id="startsAt"
					placeholder="Starts"
					autocomplete="off"
					initialValue={defaultDate}
					isRequired
				/>
				<DateTimePicker
					field="endsAt"
					id="endsAt"
					placeholder="Ends"
					autocomplete="off"
					onFocus={this.onEndsAtFocus}
				/>
				<PublishAtPicker field="publishAt" id="publishAt" placeholder="Publish Date" autocomplete="off" />
				<Tags
					field="tags"
					id="tags"
					placeholder="Tags"
					tagInstructions="Add Tags"
					allowNew
					dataObjects={sessionTags}
					dataObjectKey="id"
					dataObjectLabel="label"
					minQueryLength={1}
				/>
				{data.length > 0 && (
					<Tags
						field="topics"
						id="topics"
						placeholder="Topics"
						tagInstructions="Add Topics"
						dataObjects={data}
						dataObjectKey="key"
						dataObjectLabel="label"
					/>
				)}

				<div className="form-group">
					<em>Send an automatic reminder notification to those who have starred this session</em>
					<SelectInput field="reminder" id="reminder" values={remindOptions} />
					{values.reminder === "yes" && (
						<div>
							<TextInput
								field="alertMinutesBefore"
								id="alertMinutesBefore"
								placeholder="Minutes Before Session"
								autocomplete="off"
								initialValue={5}
							/>
						</div>
					)}
				</div>
				{!tagsFetched && (
					<div>
						<p>Fetching Parent Sessions</p>
						<Loader />
					</div>
				)}
				{tagsFetched && (
					<SelectInput
						field="parentId"
						id="parentId"
						placeholder="Parent Session"
						defaultEnabled
						values={sessionOptions}
					/>
				)}
				<em>
					Optional. Only the parent session will initially display in the schedule, however on selecting the
					parent sessions, child sessions are displayed.
				</em>

				{values.kind === "full" && (
					<React.Fragment>
						<div className="row" style={{ marginTop: "8px" }}>
							<div className="col-sm-6">
								{/*<CheckBox field="isShareable" id="isShareable" label="Shareable" /> */}
								<CheckBox field="hasNotes" id="hasNotes" label="Has Notes" />
							</div>

							<div className="col-sm-6">
								<CheckBox field="hasQA" id="hasQA" label="Has QA" />
								{/*<CheckBox field="hasPolls" id="hasPolls" label="Has Polls" />*/}
							</div>
						</div>
						{values.hasNotes && (
							<MultilineTextInput
								id="defaultNoteText"
								field="defaultNoteText"
								placeholder="Default Notes"
								helperText="Notes your attendees would see by default for this session"
								isRequired={false}
							/>
						)}
						<TextEditor field="description" id="description" placeholder="Description" autocomplete="off" />
						<FileInput field="imageUrl" id="imageUrl" placeholder="Image URL" />
						<SessionSpeakers values={values} formApi={formApi} />
						<Locations values={values} formApi={formApi} />
						{/*
							<TextAndValueArray field="resources" id="resources" placeholder="Resources" initialValue={[]} />
						*/}
					</React.Fragment>
				)}
				<div className="btn btn-success" onClick={() => this.props.submitForm(true)}>
					{this.props.buttonLabel}
				</div>
			</div>
		);
	}
}

SessionsForm.propTypes = {
	actionType: PropTypes.string,
	buttonLabel: PropTypes.string,
	cultures: PropTypes.array,
	formApi: PropTypes.any,
	id: PropTypes.string,
	initialValues: PropTypes.object,
	sessions: PropTypes.array,
	showExtraFields: PropTypes.bool,
	submitForm: PropTypes.func,
	topics: PropTypes.array,
	values: PropTypes.object,
};

export default formWrapper(SessionsForm);
